import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { Subject } from "rxjs";
import { debounceTime, first, takeUntil } from "rxjs/operators";
import { BuildingSurveyViewModel } from "src/app/shared/Models/buildingTableViewModel";
import { ExistingLomBuildingDto } from "src/app/shared/Models/read/ExistingLomBuildingDto";
import { GetLomBuildingsByAddressCriteriaQuery } from "src/app/shared/Models/read/GetLomBuildingsByAddressCriteriaQuery";
import { AssignAddressToExistingBuildingDTO } from "src/app/shared/Models/write/AssignAddressToExistingBuildingDTO";
import {
  DialogService,
  DialogType,
} from "src/app/shared/services/dialog.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { TsaRemovalService } from "src/app/shared/tsa-removal.service";
import { BuildingService } from "../../services/building.service";

@Component({
  selector: "app-reassign-address-existing-building",
  templateUrl: "./reassign-address-existing-building.component.html",
  styleUrls: ["./reassign-address-existing-building.component.scss"],
})
export class ReassignAddressExistingBuildingComponent
  implements OnInit, OnDestroy
{
  @Input() buildingSurveyVm: BuildingSurveyViewModel;
  @Input() allBuildingSurveyVms: BuildingSurveyViewModel[];
  @Input() areaId: string;
  @Output() closeReassignAddress: Subject<any> = new Subject<any>();
  @Output() reloadBuildingList: EventEmitter<any> = new EventEmitter();

  addressForm: FormGroup = new FormGroup({});
  debouncer: Subject<any> = new Subject<any>();
  matchingAddresses: ExistingLomBuildingDto[] = [];
  selectedAddress: ExistingLomBuildingDto;

  constructor(
    private _fb: FormBuilder,
    private dialogService: DialogService,
    private buildingService: BuildingService,
    private loaderService: LoaderService,
    private tsaRemovalService: TsaRemovalService
  ) {}

  destroy$ = new Subject();
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
    this.loaderService.hide();
  }

  ngOnInit() {
    this.buildForm();
    this.debouncer
      .pipe(takeUntil(this.destroy$), debounceTime(300))
      .subscribe((val) => {
        this.buildingService
          .getLomBuildingsByAddressCriteria(
            new GetLomBuildingsByAddressCriteriaQuery({
              ...val,
              areaId: this.areaId,
            })
          )
          .pipe(first())
          .subscribe((val: any[]) => (this.matchingAddresses = val));
      });
  }

  onSelectExistingBuilding(address: ExistingLomBuildingDto) {
    this.selectedAddress = address;
  }

  onClose() {
    this.closeReassignAddress.next(true);
  }

  onSave() {
    var addressesForBuilding = this.allBuildingSurveyVms.filter(
      (address) => address.buildingId === this.buildingSurveyVm.buildingId
    );

    let warningMessage = "";
    if (
      !addressesForBuilding.some(
        (address) => address.addressId !== this.buildingSurveyVm.addressId
      ) &&
      this.buildingSurveyVm.buildingGroupId
    )
      warningMessage = $localize`:@@buildinglist.reassignaddresslastofbg.detailtext:This address will be assigned to the building with lomKey: ${
        this.selectedAddress.buildingLomKey ?? "N/A"
      }. Please note that by now removing the last Address from this Building, its relation to the Building Group will be removed and Inside Survey information will be lost. Are you sure?`;
    else
      warningMessage = $localize`:@@buildinglist.reassignaddress.detailtext:This address will be assigned to the building with lomKey: ${
        this.selectedAddress.buildingLomKey ?? "N/A"
      }. Are you sure?`;

    var detailText = this.tsaRemovalService.getRemarkForExistingLomBuilding(
      this.selectedAddress,
      warningMessage
    );

    this.dialogService
      .openDialog({
        headerText: $localize`:@@buildinglist.reassignaddress.header:Reassign address`,
        detailText: detailText,
        dialogType: DialogType.Continue,
      })
      .pipe(first())
      .subscribe((feedback) => {
        this.loaderService.show();
        if (feedback)
          this.buildingService
            .assignAddressToExistingBuilding(
              new AssignAddressToExistingBuildingDTO({
                oldBuildingId: this.buildingSurveyVm.buildingId,
                newBuildingId: this.selectedAddress.buildingId,
                addressId: this.buildingSurveyVm.addressId,
              })
            )
            .pipe(first())
            .subscribe(
              () => {
                this.closeReassignAddress.next(true);
                this.reloadBuildingList.emit();
              },
              () => {
                this.loaderService.hide();
              }
            );
        else this.closeReassignAddress.next(true);
      });
  }

  private buildForm() {
    this.addressForm = this._fb.group({
      lomKey: ["", []],
      street: ["", []],
      number: ["", []],
      suffix: ["", []],
      postalCode: ["", []],
      city: ["", []],
    });

    this.addressForm.patchValue({
      ...this.buildingSurveyVm,
    });

    this.addressForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((val) => {
        this.debouncer.next(val);
      });
  }
}
